import React from 'react';
import { ARCHETYPE_META, ArchetypeIcon } from './NeuralProfile';

const DIMENSION_LABELS = {
  rawSpeed:         'Reaction Speed',
  decisionQuality:  'Decision Quality',
  aimPrecision:     'Aim Precision',
  trackingAccuracy: 'Tracking Accuracy',
  workingMemory:    'Working Memory',
  flickSpeed:       'Flick Speed',
  prioritization:   'Prioritization',
  consistency:      'Consistency',
};

/**
 * ArchetypeShareCard — identity-focused 1200x630 share card.
 * Optimised for Discord profile sharing and TikTok text overlays.
 * Rendered off-screen, captured via html2canvas.
 */
export default function ArchetypeShareCard({ profile, archetypeShareRef }) {
  const meta = ARCHETYPE_META[profile.archetype?.id] || { color: '#00d4ff', glow: 'rgba(0, 212, 255, 0.25)' };
  const { scores } = profile;

  const dimEntries = Object.entries(scores).filter(([k]) => k !== 'overallScore');
  const topDimKey = dimEntries.sort(([, a], [, b]) => b - a)[0]?.[0];
  const topDimScore = dimEntries[0]?.[1];
  const topDimLabel = topDimKey ? (DIMENSION_LABELS[topDimKey] || topDimKey) : 'Reaction Speed';

  const overallScore = scores.overallScore !== undefined
    ? scores.overallScore
    : Math.round(dimEntries.reduce((a, [, v]) => a + v, 0) / dimEntries.length);

  const color = meta.color;

  return (
    <div
      ref={archetypeShareRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: 0,
        width: 1200,
        height: 630,
        background: '#06090f',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Background radial burst */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 700,
        height: 700,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${color}18 0%, transparent 65%)`,
        pointerEvents: 'none',
      }} />

      {/* Top accent bar */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
      }} />

      {/* Bottom accent bar */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
      }} />

      {/* NeuroRank wordmark top-left */}
      <div style={{
        position: 'absolute',
        top: 36,
        left: 60,
        fontSize: 11,
        color: '#3d4f6b',
        letterSpacing: 4,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}>
        NEURORANK
      </div>

      {/* neurorank.app top-right */}
      <div style={{
        position: 'absolute',
        top: 36,
        right: 60,
        fontSize: 12,
        color: '#3d4f6b',
        fontFamily: 'monospace',
      }}>
        neurorank.app
      </div>

      {/* Main content — centered */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, padding: '0 100px' }}>
        {/* Archetype icon */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: `${color}18`,
          border: `2px solid ${color}44`,
          marginBottom: 28,
        }}>
          <ArchetypeIcon archetypeId={profile.archetype?.id} size={36} color={color} />
        </div>

        {/* "You are" label */}
        <div style={{
          fontSize: 13,
          color: '#6b7ab8',
          letterSpacing: 4,
          fontWeight: 600,
          textTransform: 'uppercase',
          marginBottom: 12,
        }}>
          Cognitive Archetype
        </div>

        {/* Archetype name — dominant */}
        <div style={{
          fontSize: 88,
          fontWeight: 900,
          color: '#f8fafc',
          lineHeight: 1,
          letterSpacing: -3,
          marginBottom: 16,
        }}>
          {profile.archetype?.name || 'Unknown'}
        </div>

        {/* Archetype tagline */}
        {profile.archetype?.tagline && (
          <div style={{
            fontSize: 20,
            color: color,
            fontWeight: 500,
            marginBottom: 32,
            letterSpacing: '0.02em',
          }}>
            {profile.archetype.tagline}
          </div>
        )}

        {/* Stat line */}
        <div style={{
          fontSize: 15,
          color: '#6b7ab8',
          marginBottom: 36,
        }}>
          {overallScore}/100 overall · top {topDimScore ? Math.round(100 - topDimScore) + '%' : 'performer'} in {topDimLabel}
        </div>

        {/* CTA */}
        <div style={{
          display: 'inline-block',
          padding: '14px 40px',
          background: color,
          borderRadius: 40,
          fontSize: 15,
          fontWeight: 800,
          color: '#000',
          letterSpacing: '0.04em',
        }}>
          Find out your archetype — free
        </div>
      </div>
    </div>
  );
}
